import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IDevice, IDeviceType, ISetup } from '../models/device.interface';
import { ICompany } from '../../companies/models/company.interface';
import { ERoleAdminString } from '../../../shared/data/role.enum';
import { DeviceService } from '../services/device.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceTypeService } from '../services/deviceType.service';
import { CompanyService } from '../../companies/services/company.service';
import { ToastService } from '../../../shared/services/toast.service';
import { SetupService } from '../services/setup.service';
import { DeviceLicenseService } from '../services/deviceLicense.service';
import { SessionService } from '../../../shared/services/session.service';
import { canPickCompany } from '../../../shared/utilities/company-scope';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';
import { EMessageType } from 'src/app/shared/data/response-default.interface';

@Component({
  selector: 'app-edit-device',
  templateUrl: './edit-device.component.html',
  styleUrls: ['./edit-device.component.scss']
})
export class EditDeviceComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  deviceId = '';
  formDevice = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    companyId: new UntypedFormControl(null, [Validators.required]),
    setupId: new UntypedFormControl(null, [Validators.required]),
    deviceTypeId: new UntypedFormControl(null, [Validators.required]),
    hardwareId: new UntypedFormControl('', [Validators.required]),
    equipmentId: new UntypedFormControl('', [Validators.required]),
    contactName: new UntypedFormControl('', [Validators.required]),
    contactPhone: new UntypedFormControl('', [Validators.required]),
    observation: new UntypedFormControl(''),
    licenseKey: new UntypedFormControl(''),
    licenseStatus: new UntypedFormControl(''),
    expirationDate: new UntypedFormControl(''),
    paymentTerminalId: new UntypedFormControl(''),
    active: new UntypedFormControl(true)
  });
  minDate: string = new Date().toISOString().split('T')[0];
  deviceTypes: IDeviceType[] = [];
  companies: ICompany[] = [];
  setupsOptions: ISetup[] = [];
  public statusOptions: string[] = [];
  // Usuário de empresa mantém a empresa do dispositivo: sem seletor de empresa nem de setup.
  podeEscolherEmpresa = false;

  public readonly licenseStatusMap: Record<string, string> = {
    issued: 'Emitida',
    active: 'Ativa',
    revoked: 'Revogada',
    expired: 'Expirada'
  };

  constructor(
    private readonly setupService: SetupService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly deviceService: DeviceService,
    private readonly deviceLicenseService: DeviceLicenseService,
    private readonly deviceTypeService: DeviceTypeService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.podeEscolherEmpresa = canPickCompany(this.sessionService.getSession());
    this.formDevice.get('licenseKey')?.disable();
    this.deviceTypeService.listDevicesTypes().subscribe((response) => {
      this.deviceTypes = response.data;
    });

    // Usuário de empresa não tem seletor, e Company/ListActive responde 403 para ele.
    if (this.podeEscolherEmpresa) {
      this.companyService
        .listarEmpresasAtivas()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.companies = response.data;
          },
          error: (erro) => {
            this.companies = [];
            if (!interceptorAvisouErro(erro)) {
              this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
            }
          }
        });
    }

    this.deviceId = this.activatedRoute.snapshot.paramMap.get('id')!;

    if (this.deviceId) {
      this.deviceService.getDeviceById(this.deviceId).subscribe((response) => {
        if (response.isSucceed) {
          this.formDevice.get('name')?.setValue(response.data.name);
          this.formDevice.get('companyId')?.setValue(response.data.companyId);
          this.formDevice.get('deviceTypeId')?.setValue(response.data.deviceTypeId);
          this.formDevice.get('hardwareId')?.setValue(response.data.hardwareId);
          this.formDevice.get('equipmentId')?.setValue(response.data.equipmentId);
          this.formDevice.get('contactName')?.setValue(response.data.contactName);
          this.formDevice.get('contactPhone')?.setValue(response.data.contactPhone);
          this.formDevice.get('licenseKey')?.setValue(response.data.licenseKey);
          this.formDevice.get('licenseStatus')?.setValue(response.data.licenseStatus);
          this.formDevice.get('expirationDate')?.setValue(response.data.expirationDate);
          this.formDevice.get('observation')?.setValue(response.data?.observation);
          this.formDevice.get('setupId')?.setValue(response.data.setupId);
          this.formDevice.get('paymentTerminalId')?.setValue(response.data.paymentTerminalId);
          this.formDevice.get('active')?.setValue(response.data.active);

          const licenseStatus = response.data.licenseStatus;
          if (licenseStatus === 'active') {
            this.statusOptions = ['active', 'revoked'];
          } else if (licenseStatus === 'issued') {
            this.statusOptions = ['issued', 'revoked'];
          } else if (licenseStatus === 'revoked') {
            this.statusOptions = ['active', 'revoked'];
          } else if (licenseStatus === 'expired') {
            this.statusOptions = ['expired'];
          }
        }
      });
    }

    this.formDevice.get('companyId')?.valueChanges.subscribe((companyId) => {
      this.formDevice.get('setupId')?.patchValue(null);
      if (companyId) {
        this.setupService.listSetupsActiveByCompany(companyId).subscribe((response) => {
          if (response.data) {
            this.setupsOptions = response.data;
          }
          this.formDevice.get('setupId')?.enable();
        });
      } else {
        this.formDevice.get('setupId')?.disable();
      }
    });
  }

  public copyLicenseKey(): void {
    const licenseKey = this.formDevice.get('licenseKey')?.value;
    if (licenseKey) {
      navigator.clipboard
        .writeText(licenseKey)
        .then(() => {
          this.toastService.toast('Chave de licença copiada com sucesso!');
        })
        .catch((err) => {
          this.toastService.toast('Erro ao copiar chave de licença.');
        });
    }
  }

  public updateLicenseStatus(newStatus: string): void {
    const licenseKey = this.formDevice.get('licenseKey')?.value;
    if (licenseKey) {
      this.deviceLicenseService.updateStatus(licenseKey, newStatus).subscribe({
        next: () => {
          this.toastService.toast('Status da licença atualizado com sucesso!');
          this.formDevice.get('licenseStatus')?.setValue(newStatus);
        },
        error: () => {
          this.toastService.toast('Erro ao atualizar status da licença.');
        }
      });
    }
  }

  public editDevice(): void {
    if (this.formDevice.invalid) {
      this.toastService.toast('Preencha todos os campos obrigatórios antes de salvar.');
      this.formDevice.markAllAsTouched();
      return;
    }

    const updateDevice: IDevice = {
      ...this.formDevice.value
    };

    if (this.deviceId) {
      this.deviceService.updateDevice(this.deviceId, updateDevice).subscribe((response) => {
        if (response.isSucceed) {
          this.toastService.toast('Dispositivo atualizado com sucesso.');
          this.router.navigateByUrl('/dash/dispositivos');
        } else {
          this.toastService.toast('Erro ao atualizar dispositivo.');
        }
      });
    }
  }
}
